/**
 * Origin → destination as two flag tiles, each tagged with its country code —
 * how a journey is drawn wherever the citizen meets it (the header switcher,
 * "My journeys", the confirmations on it).
 *
 * One `sr-only` label for the route; both flags are decorative, otherwise a
 * screen reader reads the route twice. A missing origin (a journey still in
 * `pending_setup`) is `<CountryFlag>`'s own dashed "?" placeholder.
 */
import { ArrowRight } from '@tonnie/assets';
import { useCountryName, useTranslation } from '@tonnie/locale';
import { CountryFlag, cn } from '@tonnie/ui';

type RouteTileSize = 'md' | 'sm';

interface IJourneyRouteTileProps {
  originCountry: string | null | undefined;
  destinationCountry: string | null | undefined;
  /** `md` carries the code chips; `sm` is for rows and dialogs. */
  size?: RouteTileSize;
  /** Archived journeys render desaturated. */
  muted?: boolean;
}

const TILE_CLASSES: Record<RouteTileSize, string> = {
  md: 'w-12 h-12 rounded-lg',
  sm: 'w-10 h-10 rounded-md',
};

export function JourneyRouteTile({
  originCountry,
  destinationCountry,
  size = 'md',
  muted = false,
}: IJourneyRouteTileProps) {
  const { t } = useTranslation();
  const countryName = useCountryName();
  const withCodes = size === 'md';

  return (
    <div className={cn('flex items-center gap-2 shrink-0', muted && 'grayscale')}>
      <div className="relative">
        <div
          className={cn(
            'bg-background border-2 border-border flex items-center justify-center shadow-sm overflow-hidden',
            TILE_CLASSES[size]
          )}
        >
          <CountryFlag code={originCountry} size="lg" />
        </div>
        {withCodes && originCountry && (
          <span
            aria-hidden="true"
            className="absolute -bottom-1 -right-1 px-1 h-3.5 bg-background border border-border rounded-full flex items-center justify-center text-2xs font-medium leading-none"
          >
            {originCountry}
          </span>
        )}
      </div>
      <ArrowRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" aria-hidden="true" />
      <div className="relative">
        <div
          className={cn(
            'bg-linear-to-br from-primary/20 to-primary/5 border-2 border-primary/40 flex items-center justify-center shadow-md overflow-hidden',
            TILE_CLASSES[size]
          )}
        >
          <CountryFlag code={destinationCountry} size="lg" />
        </div>
        {withCodes && destinationCountry && (
          <span
            aria-hidden="true"
            className="absolute -bottom-1 -right-1 px-1 h-3.5 bg-primary border border-primary text-primary-foreground rounded-full flex items-center justify-center text-2xs font-bold leading-none"
          >
            {destinationCountry}
          </span>
        )}
      </div>
      <span className="sr-only">
        {t('common.routeSrLabel', {
          origin: countryName(originCountry ?? null),
          destination: countryName(destinationCountry ?? null),
        })}
      </span>
    </div>
  );
}
